import { FC, useEffect, useState, useMemo, Fragment } from 'react';
import dayjs, { Dayjs } from 'dayjs';
// Models
import IQuiz from 'models/Quiz';
// Redux
import { useAppSelector } from 'hooks/useStore';
// Selectors
import { selectQuiz, selectIsQuizStarted, selectIsQuizEnded } from 'store/quiz/quizSelectors';
// Components
import Circle from 'components/Circle';
// Utilities
import { getTimeRemaining } from 'utilities/Utilities';
// i18next
import { useTranslation } from 'react-i18next';
import QuizTypes from 'types/QuizTypes';
import i18n from 'i18n';

let countdownInterval:any;
const milliseconds = 1000;

const radius = 82;
const size = radius * 2 + 12;
const strokeDasharray = (2 * Math.PI * radius);

let now = dayjs();

const Countdown:FC = () => {
  const { t } = useTranslation('common');
  // State
  const quiz:IQuiz | null = useAppSelector(selectQuiz);
  const isQuizStarted:boolean | undefined = useAppSelector(selectIsQuizStarted);
  const isQuizEnded:boolean | undefined = useAppSelector(selectIsQuizEnded);

  const startDate = useMemo(() => {
    if ( !quiz ) return null;
    return quiz.startsDate ? dayjs(Number(quiz.startsDate)) : null;
    // eslint-disable-next-line
  }, []);
  const endDate = useMemo(() => {
    if ( !quiz ) return null;
    return quiz.endsDate ? dayjs(Number(quiz.endsDate)) : null;
    // eslint-disable-next-line
  }, []);

  const prefix = quiz?.type === QuizTypes.Prediction
    ? 'prediction_'
      : quiz?.type === QuizTypes.Poll
      ? 'poll_'
    : '';

  const [ strokeDashoffset, setStrokeDashoffset ] = useState(0);
  const [ startCountdown, setStartCountdown ] = useState(false);
  const [ finalDate, setFinalDate ] = useState<Dayjs | null>(null);
  const [ label, setLabel ] = useState(`${t('quiz.startPage.quizNotYetStarted')}`);
  const [ isInitialized, setIsInitialized ] = useState(false);

  const [ days, setDays ] = useState(0);
  const [ hours, setHours ] = useState(0);
  const [ minutes, setMinutes ] = useState(0);

  useEffect(() => {
    if (isInitialized) {
      setIsInitialized(false);
    }

    if ( typeof isQuizStarted === 'undefined' ) {
      setIsInitialized(true);
      setLabel(t('quiz.startPage.quizNotYetStarted'))
      return;
    }

    if ( !quiz ) return;
    if ( !quiz.questions || !quiz.questions.length ) return;

    if ( !isQuizStarted ){
      setLabel(t(`components.countdown.${prefix}quizStarts`));
      setFinalDate(startDate);
      setStartCountdown(true);
    } else if ( isQuizEnded ){
      setLabel(t(`components.countdown.${prefix}quizEnded`));
      setIsInitialized(true);
    } else {
      setLabel(t(`components.countdown.${prefix}quizEndsIn`));
      if ( endDate ){
        setStartCountdown(true);
        setFinalDate(endDate);
      } else {
        setIsInitialized(true);
      }
    }
    // eslint-disable-next-line
  }, [i18n.language]);

  useEffect(() => {
    if ( quiz && quiz.type === QuizTypes.Personality ) return;

    if ( startCountdown ){
      countdownInterval = setInterval(() => {
        now = dayjs();
        if ( finalDate ){
          if ( now.isAfter(finalDate) ){
            setLabel(t(`components.countdown.${prefix}quizEnded`));
            clearInterval(countdownInterval);
            setStrokeDashoffset(0);
          } else {
            const remaining = getTimeRemaining(now, finalDate);

            setDays(remaining.days);
            setHours(remaining.hours);
            setMinutes(remaining.minutes);
  
            const percent = remaining.seconds * 100/60;
  
            setStrokeDashoffset(percent * strokeDasharray / 100);
          }
          setIsInitialized(true);
        }
      }, milliseconds);
    }
    // eslint-disable-next-line
  }, [startCountdown, i18n.language]);

  // if ( !startCountdown ) return null;
  if (isQuizStarted && !quiz?.endsDate) return null;
  if ( quiz && quiz.type === QuizTypes.Personality ) return (
    <Fragment>
      {!isQuizStarted && !isQuizEnded ? (
        <span className="block text-gray-500 text-xs text-center mb-4">{t('quiz.startPage.quizNotYetStarted')}</span>
      ) : null}
      {isQuizEnded ? (
        <span className="block text-gray-500 text-xs text-center mb-4">{t('quiz.startPage.finished')}</span>
      ) : null}
    </Fragment>
  );
  return (
    <div className="flex justify-center mb-4">
      <div
        className="
          relative
          flex items-center justify-center
          w-48 h-48
          shadow-neu-content
          rounded-full
          mx-auto
        "
      >
        <Circle
          size={size}
          radius={radius}
          rootStyle={{
            zIndex: 2
          }}
          circleStyle={{
            strokeDasharray,
            strokeDashoffset: `${strokeDasharray - strokeDashoffset}`,
            strokeWidth: 10
          }}
        />
       <Circle
          size={size}
          radius={radius}
          rootStyle={{
            zIndex: 1
          }}
          circleStyle={{
            strokeDasharray,
            strokeDashoffset: 0,
            strokeWidth: 6,
            opacity: 0.2
          }}
        />
        <div className="relative z-10" style={{ width: '70%' }}>
          {isInitialized && (
            <Fragment>
              <span className="block text-xs text-gray-600 text-center">{label}</span>
              {finalDate && !isQuizEnded ? (
                <div className="flex items-center">
                  <div className="flex flex-col items-center w-1/3">
                    <span className="text-lg font-bold">{days || '0'}</span>
                    <span className="text-xss text-gray-600 leading-3">{t('components.countdown.days')}</span>
                  </div>
                  <div className="flex flex-col items-center w-1/3">
                    <span className="text-lg font-bold">{hours || '0'}</span>
                    <span className="text-xss text-gray-600 leading-3">{t('components.countdown.hours')}</span>
                  </div>
                  <div className="flex flex-col items-center w-1/3">
                    <span className="text-lg font-bold">{minutes || '0'}</span>
                    <span className="text-xss text-gray-600 leading-3">{t('components.countdown.minutes')}</span>
                  </div>
                </div>
              ) : null}
            </Fragment>
          )}
        </div>
      </div>
    </div>
  )
}

export default Countdown;
